import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Megaphone, Trash2 } from 'lucide-react';

export default function AnnouncementManager() {
  const [announcements, setAnnouncements] = useState([]);
  const [newAnnouncement, setNewAnnouncement] = useState({ title: '', message: '', zoom_link: '' });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/announcements`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAnnouncements(data.announcements || []);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
    }
  };

  const addAnnouncement = async () => {
    if (!newAnnouncement.title || !newAnnouncement.message) {
      toast.error('Title and message required');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/announcements`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token')}`
        },
        body: JSON.stringify(newAnnouncement)
      });

      if (response.ok) {
        toast.success('Announcement posted!');
        setNewAnnouncement({ title: '', message: '', zoom_link: '' });
        fetchAnnouncements();
      }
    } catch (error) {
      toast.error('Error posting announcement');
    }
  };

  const deleteAnnouncement = async (id) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/announcements/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('admin_token')}` }
      });

      if (response.ok) {
        toast.success('Announcement deleted');
        fetchAnnouncements();
      }
    } catch (error) {
      toast.error('Error deleting announcement');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Megaphone className="h-5 w-5" />
          Announcement Board
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="bg-blue-50 border border-blue-200 rounded p-4">
          <h3 className="font-semibold mb-3">Post New Announcement</h3>
          <div className="space-y-3">
            <Input 
              value={newAnnouncement.title}
              onChange={(e) => setNewAnnouncement({...newAnnouncement, title: e.target.value})}
              placeholder="Announcement title"
            />
            <Textarea 
              value={newAnnouncement.message}
              onChange={(e) => setNewAnnouncement({...newAnnouncement, message: e.target.value})}
              placeholder="Announcement message"
              className="min-h-[100px]"
            />
            <Input 
              value={newAnnouncement.zoom_link}
              onChange={(e) => setNewAnnouncement({...newAnnouncement, zoom_link: e.target.value})}
              placeholder="Zoom link (optional)"
            />
            <Button onClick={addAnnouncement} className="w-full bg-green-600 hover:bg-green-700">
              📢 Post Announcement
            </Button>
          </div>
        </div>

        <div className="space-y-3">
          <h3 className="font-semibold">Current Announcements</h3>
          {announcements.length === 0 ? (
            <p className="text-gray-500 text-sm">No announcements yet</p>
          ) : (
            announcements.map((announcement) => (
              <Card key={announcement.id}>
                <CardContent className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-semibold">{announcement.title}</h4>
                    <Button
                      onClick={() => deleteAnnouncement(announcement.id)}
                      variant="outline"
                      size="sm"
                      className="text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">{announcement.message}</p>
                  {announcement.zoom_link && (
                    <a href={announcement.zoom_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 text-sm hover:underline">
                      🔗 {announcement.zoom_link}
                    </a>
                  )}
                  <p className="text-xs text-gray-500 mt-2">{new Date(announcement.created_at).toLocaleDateString()}</p>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
